/**
 * The one database this application keeps between visits (US-33.4).
 *
 * <p>There is exactly one, opened in exactly one place, and that is the whole point of this file. A
 * second `indexedDB.open("roastery-app", …)` elsewhere would carry a second version number, and the
 * two would fight over every upgrade: whichever opened first wins, the other gets
 * `VersionError`, and the feature behind it stops working on a device that has both. So the schema
 * lives here and everybody else asks for a store.
 *
 * <p>Stores, and who owns them:
 * <ul>
 *   <li>{@code artisan-sync}, {@code settings} — the Artisan folder watcher (US-05.5, ADR-011)</li>
 *   <li>{@code stocktake-drafts} — a count in progress in warehouse mode (US-33.3)</li>
 *   <li>{@code snapshots} — the last answer to a request a screen may be opened without (US-33.4)</li>
 *   <li>{@code queue} — writes made with no network, waiting for one (US-33.4)</li>
 * </ul>
 *
 * <p>Written against the native API: two hundred lines of wrapper for five object stores would be a
 * dependency to keep rather than a problem solved.
 */

const DATABASE = "roastery-app";

/**
 * 1 — the Artisan watcher (US-05.5). 2 — warehouse drafts (US-33.3). 3 — snapshots and the offline
 * queue (US-33.4). `onupgradeneeded` creates whatever is missing, so a browser arriving from any of
 * them ends up in the same place; nothing is ever renamed or dropped, because a browser that has been
 * offline for a week is a browser that skipped a version.
 */
const DATABASE_VERSION = 3;

export const ARTISAN_FILES_STORE = "artisan-sync";
export const SETTINGS_STORE = "settings";
export const STOCKTAKE_DRAFTS_STORE = "stocktake-drafts";
export const SNAPSHOTS_STORE = "snapshots";
export const QUEUE_STORE = "queue";

let connection: Promise<IDBDatabase> | null = null;

export function openDatabase(): Promise<IDBDatabase> {
  connection ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(ARTISAN_FILES_STORE)) {
        database.createObjectStore(ARTISAN_FILES_STORE, { keyPath: "key" });
      }
      if (!database.objectStoreNames.contains(SETTINGS_STORE)) {
        database.createObjectStore(SETTINGS_STORE);
      }
      if (!database.objectStoreNames.contains(STOCKTAKE_DRAFTS_STORE)) {
        database.createObjectStore(STOCKTAKE_DRAFTS_STORE, { keyPath: "locationId" });
      }
      if (!database.objectStoreNames.contains(SNAPSHOTS_STORE)) {
        // Keyed by user and request, so two people on one tablet do not read each other's roastery.
        database.createObjectStore(SNAPSHOTS_STORE, { keyPath: "key" });
      }
      if (!database.objectStoreNames.contains(QUEUE_STORE)) {
        const queue = database.createObjectStore(QUEUE_STORE, { keyPath: "id" });
        // The queue is walked in the order things happened: a drop before its start is nonsense.
        queue.createIndex("createdAt", "createdAt");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB unavailable"));
  });
  return connection;
}

/** One request in one transaction — the shape almost every read and write here has. */
export function run<T>(
  store: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDatabase().then(
    (database) =>
      new Promise<T>((resolve, reject) => {
        const transaction = database.transaction(store, mode);
        const request = work(transaction.objectStore(store));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
      }),
  );
}

/**
 * Everything in a store, or everything whose key starts with a prefix.
 *
 * <p>Both snapshots and queue entries are keyed by the user they belong to, and every question asked
 * of them is "mine, please" — so the prefix is not a convenience, it is how the two users of a shared
 * tablet stay apart.
 */
export async function allByPrefix<T>(store: string, prefix: string): Promise<T[]> {
  try {
    const rows = await run<T[]>(store, "readonly", (objectStore) =>
      objectStore.getAll(IDBKeyRange.bound(prefix, `${prefix}￿`)),
    );
    return rows ?? [];
  } catch {
    // Storage switched off: no history, and every screen still has to work.
    return [];
  }
}

export async function deleteByPrefix(store: string, prefix: string): Promise<void> {
  try {
    await run(store, "readwrite", (objectStore) =>
      objectStore.delete(IDBKeyRange.bound(prefix, `${prefix}￿`)),
    );
  } catch {
    // As above.
  }
}
